import { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons/faTriangleExclamation';
import { faArrowTrendUp } from '@fortawesome/pro-solid-svg-icons/faArrowTrendUp';
import { faArrowTrendDown } from '@fortawesome/pro-solid-svg-icons/faArrowTrendDown';
import { faDash } from '@fortawesome/pro-solid-svg-icons/faDash';
import { colorPalette } from 'shared/constants';
import { TREND_STATUS } from 'shared/constants/quality-metric.constants';
import {
  adjustTrend,
  displayGoal,
  displayPercentage,
  formatTrendPeriod,
  isSuccess,
} from 'shared/utils/quality-metrics.utils';
import { formatNumber } from 'shared/utils';
import { useDateFormatter } from 'shared/hooks/dates.hooks';
import { useMetricUsersTrends } from 'shared/hooks/quality-metric.hooks';
import { MetricGraph } from 'src/components/quality-metric/quality-metric-card/graph-over-time';
import { Divider } from 'src/components/design-system';
import { useQualityMetricsManagementContext } from '../quality-metrics-management.context';
import { ManagementDataSourceSection } from './management-data-source-section';
import { ManagementUsersTable } from './management-users-table';
import { Stat } from './management-stat';
import styles from '../quality-metrics-management.module.scss';

const TREND_ICON = {
  [TREND_STATUS.UP]: faArrowTrendUp,
  [TREND_STATUS.DOWN]: faArrowTrendDown,
  [TREND_STATUS.NO_CHANGE]: faDash,
};

// Five qualitative hues for the compared users, deliberately none of them the
// green/red the goal colouring already owns, nor the grey of the group line.
const COMPARE_COLORS = ['#1f77b4', '#9467bd', '#e377c2', '#8c564b', '#17becf'];

function trendLabel(trend) {
  if (!trend || trend.status === TREND_STATUS.NA) return null;
  if (trend.status === TREND_STATUS.NO_CHANGE) return '0%';
  return `${trend.delta > 0 ? '+' : ''}${trend.delta}%`;
}

/**
 * The group's standing on the selected metric. Deliberately not the clinician
 * `OverviewTab`: that one leads with the viewer's own score, which is
 * meaningless for someone administering another team's metric.
 */
export function ManagementOverviewTab() {
  const {
    selectedMetric,
    groupTrendDelta,
    processedGroupTrends,
    goalPercentage,
    comparedUserIds,
    setComparedUserIds,
    lang,
    metricLang,
  } = useQualityMetricsManagementContext();
  const { formatLongDate } = useDateFormatter();

  const metric = selectedMetric;

  // Users picked in the table below, overlaid on the group line. Held in the
  // URL by the context, which clears them when the metric changes.
  const { data: comparedUsers = [] } = useMetricUsersTrends(metric.id, comparedUserIds);

  // `MetricGraph` renders a spinner forever when handed an empty series, so an
  // unplotted metric gets an explicit empty state instead of the chart.
  const hasTrendData = processedGroupTrends.length > 0;

  const graphSeries = useMemo(() => {
    const group = {
      label: metricLang.DEP_CASES,
      trends: processedGroupTrends,
      color: colorPalette.neutral5,
      pointRadius: 4,
      // Only the group line keeps the pass/fail point colouring; the user
      // lines need their own colour to stay tellable apart.
      goalColoredPoints: true,
    };

    return [
      group,
      ...comparedUsers.map((user, index) => ({
        label: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email,
        // Same null-dropping and inversion the group series goes through, so
        // the lines are directly comparable.
        trends: (user.trends ?? [])
          .filter(({ rate }) => rate !== null)
          .map(adjustTrend(metric.invert)),
        color: COMPARE_COLORS[index % COMPARE_COLORS.length],
        pointRadius: 3,
      })),
    ];
  }, [metricLang, processedGroupTrends, comparedUsers, metric.invert]);

  const passing = isSuccess({
    total: metric.totalGroupRecords,
    success: metric.totalGroupSuccess,
    groupGoal: metric.groupGoal,
  });

  // `calculateCases` needs a user metric, so the group's compliant-case count
  // is derived here: inverted metrics count the failures as the good outcome.
  const compliantCases = metric.invert
    ? metric.totalGroupRecords - metric.totalGroupSuccess
    : metric.totalGroupSuccess;

  const iconColor = passing ? colorPalette.greenPositive : colorPalette.redNegative;
  const delta = trendLabel(groupTrendDelta);
  const deltaIcon = groupTrendDelta && TREND_ICON[groupTrendDelta.status];

  const currentPeriod = formatTrendPeriod(
    groupTrendDelta?.currentMonth,
    groupTrendDelta?.currentYear,
  );
  const previousPeriod = formatTrendPeriod(
    groupTrendDelta?.comparisonMonth,
    groupTrendDelta?.comparisonYear,
  );
  const trendVsLabel =
    currentPeriod && previousPeriod
      ? metricLang.TREND_TOOLTIP.replace('{currentPeriod}', currentPeriod).replace(
          '{previousPeriod}',
          previousPeriod,
        )
      : null;

  return (
    <>
      <div className={styles.overview}>
        <div className={styles.overviewCard}>
          <div className={styles.overviewSubheader}>
            <span className={styles.goalText}>
              {metricLang.GOAL}{' '}
              {displayGoal({ groupGoal: metric.groupGoal, invert: metric.invert })}
            </span>
            {trendVsLabel && <span className={styles.muted}>{trendVsLabel}</span>}
          </div>

          <Divider />

          <div className={styles.scoreRow}>
            <FontAwesomeIcon
              icon={passing ? faCheckCircle : faTriangleExclamation}
              color={iconColor}
            />
            <span className={styles.scorePercentage} style={{ color: iconColor }}>
              {displayPercentage({
                total: metric.totalGroupRecords,
                success: metric.totalGroupSuccess,
                invert: metric.invert,
              })}
            </span>
            {delta ? (
              <span className={styles.scoreTrend}>
                {delta}
                {deltaIcon && <FontAwesomeIcon icon={deltaIcon} />}
              </span>
            ) : (
              // NA means fewer than two months cleared MIN_CASES_FOR_TREND.
              <span className={styles.muted}>{lang.NOT_ENOUGH_DATA}</span>
            )}
            <span className={styles.muted}>
              {formatNumber(compliantCases)} {metricLang.OF}{' '}
              {formatNumber(metric.totalGroupRecords)} {metricLang.CASES}
            </span>
          </div>

          <Divider />

          <p className={styles.graphTitle}>{metricLang.CASES_OVER_TIME}</p>
          {hasTrendData ? (
            <MetricGraph
              goal={goalPercentage}
              invert={metric.invert}
              series={graphSeries}
              totalRecords={metric.totalGroupRecords}
              minimumRecords={0}
              hideLabel
            />
          ) : (
            <p className={styles.emptyText}>{lang.NOT_ENOUGH_DATA}</p>
          )}
        </div>

        <div className={styles.configCard}>
          <h3 className={styles.sectionTitle}>{lang.CONFIGURATION}</h3>
          <div className={styles.statGrid}>
            <Stat label={lang.FIELD_START_DATE} value={formatLongDate(metric.startDate)} />
            <Stat label={lang.FIELD_MIN_RECORDS} value={formatNumber(metric.minimumRecords)} />
            <Stat label={lang.FIELD_PRIORITY} value={formatNumber(metric.priority)} />
            <Stat
              label={lang.FIELD_INVERT}
              value={metric.invert ? lang.YES : lang.NO}
              hint={metric.invert ? lang.INVERTED_METRIC : undefined}
            />
            <Stat label={lang.FIELD_HIDDEN} value={metric.isHidden ? lang.YES : lang.NO} />
            <Stat
              label={metricLang.RESOURCES}
              value={formatNumber(metric.resources?.length || 0)}
            />
          </div>

          {/* Keyed by metric so the draft re-seeds instead of following the
              selection across metrics. */}
          <ManagementDataSourceSection key={metric.id} metric={metric} />

          {metric.description && (
            <>
              <h3 className={styles.sectionTitle}>
                {metricLang.ABOUT_METRIC.replace('{metricName}', metric.title.toLowerCase())}
              </h3>
              <p className={styles.sectionText}>{metric.description}</p>
            </>
          )}

          {metric.compliance && (
            <>
              <h3 className={styles.sectionTitle}>{metricLang.COMPLIANCE}</h3>
              <p className={styles.sectionText}>{metric.compliance}</p>
            </>
          )}
        </div>
      </div>

      {/* Keyed by metric so the pager and row selection reset on a new metric. */}
      <ManagementUsersTable
        key={metric.id}
        selectedUserIds={comparedUserIds}
        onSelectedUserIdsChange={setComparedUserIds}
      />
    </>
  );
}
